import classNames from 'classnames'
import React from 'react'
import { Icon, Tooltip } from '@blueprintjs/core'
import { IconNames, IconName } from '@blueprintjs/icons'

import { BlindMatchStatus, type StatusTooltipOverrides } from 'shared-libs/components/ui2/fields/ui2-blind-match-field'
import { LoadingSpinner, LoadingSpinnerSize } from 'browser/components/atomic-elements/atoms/loading-spinner/loading-spinner'
import { StatusBadge } from 'browser/components/atomic-elements/atoms/status-badge/status-badge'

import './_blind-match-status-badge.scss'

const VectorLogo = require('images/navigation/vector-logo-white.svg')

interface IBlindMatchStatusBadgeProps {
  status: BlindMatchStatus
  // Per-state tooltip copy overrides — any key omitted falls through to
  // the default in `statusToTooltip`.
  statusTooltips?: StatusTooltipOverrides
}

// Maps each blind-match state to a semantic StatusBadge color. We use the
// shared semantic palette (info/success/danger/default) rather than raw
// hues so the badge tracks the rest of the design system.
const statusToColor: Record<BlindMatchStatus, string | undefined> = {
  [BlindMatchStatus.Loading]: undefined, // Loading renders as a bare spinner.
  [BlindMatchStatus.Validating]: undefined, // Validating also renders as a bare spinner.
  [BlindMatchStatus.Precheck]: 'default',
  [BlindMatchStatus.Failed]: 'danger',
  [BlindMatchStatus.Verified]: 'success',
  [BlindMatchStatus.Overridden]: 'amber',
  [BlindMatchStatus.Unavailable]: 'transparent',
  [BlindMatchStatus.FetchError]: 'danger',
}

const statusToText: Record<BlindMatchStatus, string> = {
  [BlindMatchStatus.Loading]: 'Verifying…',
  [BlindMatchStatus.Validating]: 'Validating…',
  [BlindMatchStatus.Precheck]: 'Ready to Verify',
  [BlindMatchStatus.Failed]: 'Match Failed',
  [BlindMatchStatus.Verified]: 'Match Verified',
  [BlindMatchStatus.Overridden]: 'Overridden',
  [BlindMatchStatus.Unavailable]: 'Match Unavailable',
  [BlindMatchStatus.FetchError]: 'Unable to Verify',
}

const statusToTooltip: Record<BlindMatchStatus, string> = {
  [BlindMatchStatus.Loading]: 'Verifying the entered value against the source.',
  [BlindMatchStatus.Validating]: 'Validating the entered value against the source.',
  [BlindMatchStatus.Precheck]: 'Type a value to verify it against the hidden source.',
  [BlindMatchStatus.Failed]: 'The value entered does not match the source value.',
  [BlindMatchStatus.Verified]: 'The entered value matches the source.',
  [BlindMatchStatus.Overridden]: 'The value does not match the source, but was overridden with a reason.',
  [BlindMatchStatus.Unavailable]: 'No source value is available to verify against.',
  [BlindMatchStatus.FetchError]: 'Could not load source for verification. Saving is disabled until this resolves.',
}

// Precheck is special — it uses the Vector brand mark instead of a
// Blueprint icon. The rest map to Blueprint icons rendered via <Icon>.
const statusToIcon: Record<BlindMatchStatus, IconName | null> = {
  [BlindMatchStatus.Loading]: null,
  [BlindMatchStatus.Validating]: null,
  [BlindMatchStatus.Precheck]: null,
  [BlindMatchStatus.Failed]: IconNames.DELETE,
  [BlindMatchStatus.Verified]: IconNames.ENDORSED,
  [BlindMatchStatus.Overridden]: IconNames.ISSUE_CLOSED,
  [BlindMatchStatus.Unavailable]: null,
  [BlindMatchStatus.FetchError]: IconNames.DELETE,
}

// Reusable badge for any field that participates in blind-match behavior
// (BlindMatchField, future barcode/QR opt-in, etc). Renders via the
// shared StatusBadge atom so colors track the rest of the system.
export const BlindMatchStatusBadge: React.FC<IBlindMatchStatusBadgeProps> = ({ status, statusTooltips }) => {
  const badge = renderBadge(status)
  const tooltipText = statusTooltips?.[status] ?? statusToTooltip[status]
  // Tooltip is the canonical place for the longer explanation of what each
  // state means. Click-only so the interaction matches the NM tap-to-open
  // popover — no hover-only behavior that touch users would miss.
  return (
    <Tooltip content={tooltipText} placement="top" interactionKind="click">
      {badge}
    </Tooltip>
  )
}

function renderBadge(status: BlindMatchStatus): JSX.Element {
  if (status === BlindMatchStatus.Loading || status === BlindMatchStatus.Validating) {
    return (
      <span className="c-blindMatchStatusBadge c-blindMatchStatusBadge--loading">
        <span>{statusToText[status]}</span>
        <LoadingSpinner size={LoadingSpinnerSize.XS} />
      </span>
    )
  }
  const iconName = statusToIcon[status]
  const isPrecheck = status === BlindMatchStatus.Precheck
  return (
    <StatusBadge
      className={classNames('c-blindMatchStatusBadge', {
        'c-blindMatchStatusBadge--precheck': status === BlindMatchStatus.Precheck,
      })}
      color={statusToColor[status]}
    >
      {isPrecheck && (
        <span className="c-blindMatchStatusBadge-icon">
          <img className="c-blindMatchStatusBadge-vectorLogo" src={VectorLogo} />
        </span>
      )}
      {iconName && (
        <span className="c-blindMatchStatusBadge-icon">
          <Icon icon={iconName} />
        </span>
      )}
      <span>{statusToText[status]}</span>
    </StatusBadge>
  )
}
